import styled from 'styled-components';
import { useState, useEffect, FormEvent, useMemo } from 'react';
import { Link } from 'react-router-dom';
import Icon from '@mdi/react';
import {
  mdiKeyVariant,
  mdiRefresh,
  mdiLogout,
  mdiDelete,
  mdiContentCopy,
  mdiAccountAlert,
  mdiCheckCircle,
  mdiCancel,
  mdiPlus,
} from '@mdi/js';
import {
  collection,
  query,
  orderBy,
  getDocs,
  doc,
  setDoc,
  updateDoc,
  deleteDoc,
  serverTimestamp,
  Timestamp,
} from 'firebase/firestore';
import { db } from '../../../firebase';
import { useAuth } from '../../../context/AuthContext';
import {
  hashCredential,
  isExcludedEmail,
  TERRA_CREDENTIALS,
  TERRA_ACCESS_LOGS,
} from '../../../lib/terraAccess';
import { GlassCard } from '../../ui/GlassCard';
import { Button } from '../../ui/Button';
import { Container } from '../../ui/Container';
import { PageHead } from '../../seo';

const PageWrapper = styled.div`
  padding-top: 73px;
  min-height: 100vh;
`;

const Header = styled.header`
  padding: var(--space-6) 0;
  border-bottom: 1px solid var(--color-border);
  margin-bottom: var(--space-8);
`;

const HeaderContent = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: var(--space-4);
`;

const HeaderTitle = styled.h1`
  font-size: var(--text-2xl);
  display: flex;
  align-items: center;
  gap: var(--space-3);
`;

const HeaderActions = styled.div`
  display: flex;
  align-items: center;
  gap: var(--space-3);
`;

const Section = styled.section`
  margin-bottom: var(--space-10);
`;

const SectionTitle = styled.h2`
  font-size: var(--text-xl);
  margin-bottom: var(--space-4);
`;

const SectionHint = styled.p`
  font-size: var(--text-sm);
  color: var(--color-text-tertiary);
  margin-bottom: var(--space-4);
`;

const IssueForm = styled.form`
  display: grid;
  grid-template-columns: 1.4fr 1fr 1.2fr auto;
  gap: var(--space-3);
  align-items: end;

  @media (max-width: 820px) {
    grid-template-columns: 1fr;
  }
`;

const Field = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--space-1);
`;

const Label = styled.label`
  font-size: var(--text-xs);
  color: var(--color-text-tertiary);
`;

const Input = styled.input`
  width: 100%;
  padding: var(--space-3) var(--space-4);
  border: 1px solid var(--color-border);
  border-radius: var(--radius-md);
  background: var(--color-background);
  color: var(--color-text-primary);
  font-size: var(--text-base);

  &:focus {
    outline: none;
    border-color: var(--color-brand-primary);
  }
`;

const PasswordRow = styled.div`
  display: flex;
  gap: var(--space-2);
`;

const TinyButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: var(--space-1);
  padding: 0 var(--space-2);
  border: 1px solid var(--color-border);
  border-radius: var(--radius-md);
  background: var(--color-background-secondary);
  color: var(--color-text-secondary);
  font-size: var(--text-xs);
  cursor: pointer;

  &:hover {
    background: var(--color-border);
  }
`;

const Banner = styled.div`
  display: flex;
  align-items: center;
  gap: var(--space-2);
  padding: var(--space-3) var(--space-4);
  border-radius: var(--radius-md);
  background: rgba(52, 199, 89, 0.12);
  color: var(--color-success);
  font-size: var(--text-sm);
  margin-top: var(--space-4);
`;

const TableWrap = styled.div`
  overflow-x: auto;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: var(--text-sm);

  th,
  td {
    text-align: left;
    padding: var(--space-3) var(--space-4);
    border-bottom: 1px solid var(--color-border);
    white-space: nowrap;
  }

  th {
    color: var(--color-text-tertiary);
    font-weight: var(--font-medium);
  }

  tbody tr:hover {
    background: var(--color-background-secondary);
  }
`;

const Pill = styled.span<{ $tone: 'ok' | 'warn' | 'muted' }>`
  display: inline-flex;
  align-items: center;
  gap: var(--space-1);
  padding: 2px var(--space-2);
  border-radius: var(--radius-sm);
  font-size: var(--text-xs);
  font-weight: var(--font-medium);
  background: ${(p) =>
    p.$tone === 'warn'
      ? 'rgba(255, 59, 48, 0.14)'
      : p.$tone === 'ok'
        ? 'rgba(52, 199, 89, 0.14)'
        : 'var(--color-background-secondary)'};
  color: ${(p) =>
    p.$tone === 'warn'
      ? 'var(--color-error)'
      : p.$tone === 'ok'
        ? 'var(--color-success)'
        : 'var(--color-text-tertiary)'};
`;

const Mono = styled.span`
  font-family: var(--font-mono);
  font-size: var(--text-xs);
`;

const EmptyState = styled.div`
  text-align: center;
  padding: var(--space-10);
  color: var(--color-text-tertiary);
`;

const Stats = styled.div`
  display: grid;
  grid-template-columns: repeat(5, 1fr);
  gap: var(--space-4);
  margin-bottom: var(--space-8);

  @media (max-width: 900px) {
    grid-template-columns: repeat(3, 1fr);
  }
  @media (max-width: 560px) {
    grid-template-columns: repeat(2, 1fr);
  }
`;

const StatCard = styled(GlassCard)`
  text-align: center;
`;

const StatValue = styled.div`
  font-size: var(--text-3xl);
  font-weight: var(--font-bold);
  color: var(--color-brand-primary);
`;

const StatLabel = styled.div`
  font-size: var(--text-sm);
  color: var(--color-text-tertiary);
`;

interface Credential {
  id: string;
  email: string;
  label: string;
  password?: string;
  active: boolean;
  createdAt?: Timestamp;
  sentAt?: Timestamp | null;
}

interface AccessLog {
  id: string;
  email: string;
  label: string;
  credentialId: string;
  success: boolean;
  event?: string;
  ip: string;
  browser: string;
  os: string;
  deviceType: string;
  timezone: string;
  language: string;
  screen: string;
  referrer: string;
  createdAt?: Timestamp;
}

interface RecipientSummary {
  credentialId: string;
  email: string;
  label: string;
  opens: number;
  ips: string[];
  devices: string[];
  lastSeen?: Timestamp;
  shared: boolean;
}

function genPassword(): string {
  const alphabet = 'ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnopqrstuvwxyz23456789';
  const bytes = new Uint8Array(16);
  crypto.getRandomValues(bytes);
  let out = '';
  for (let i = 0; i < bytes.length; i++) {
    out += alphabet[bytes[i] % alphabet.length];
    if (i === 3 || i === 7 || i === 11) out += '-';
  }
  return out;
}

function formatDate(ts?: Timestamp): string {
  if (!ts) return '—';
  return ts.toDate().toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

// "5 days ago", "4 hours ago", "just now", "in 2 hours" (future).
function relativeTime(ts?: Timestamp | null): string {
  if (!ts) return '';
  const diffMs = Date.now() - ts.toMillis();
  const sec = Math.round(Math.abs(diffMs) / 1000);
  const min = Math.round(sec / 60);
  const hr = Math.round(min / 60);
  const day = Math.round(hr / 24);

  let label: string;
  if (sec < 45) return diffMs < 0 ? 'in a moment' : 'just now';
  else if (sec < 90) label = '1 minute';
  else if (min < 45) label = `${min} minutes`;
  else if (min < 90) label = '1 hour';
  else if (hr < 24) label = `${hr} hours`;
  else if (hr < 36) label = '1 day';
  else if (day < 30) label = `${day} days`;
  else if (day < 365) label = `${Math.round(day / 30)} months`;
  else label = `${Math.round(day / 365)} years`;

  return diffMs < 0 ? `in ${label}` : `${label} ago`;
}

// Timestamp -> value for <input type="datetime-local"> (local time, no seconds).
function toLocalInput(ts?: Timestamp | null): string {
  if (!ts) return '';
  const d = ts.toDate();
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

export function TerraAccessAdmin() {
  const { logout } = useAuth();
  const [credentials, setCredentials] = useState<Credential[]>([]);
  const [logs, setLogs] = useState<AccessLog[]>([]);
  const [loading, setLoading] = useState(true);

  const [email, setEmail] = useState('');
  const [label, setLabel] = useState('');
  const [password, setPassword] = useState(genPassword());
  const [issuing, setIssuing] = useState(false);
  const [issued, setIssued] = useState<{ email: string; password: string } | null>(null);
  const [formError, setFormError] = useState('');

  const fetchAll = async () => {
    setLoading(true);
    try {
      const credSnap = await getDocs(collection(db, TERRA_CREDENTIALS));
      setCredentials(
        credSnap.docs.map((d) => ({ id: d.id, ...d.data() })) as Credential[],
      );
      const logSnap = await getDocs(
        query(collection(db, TERRA_ACCESS_LOGS), orderBy('createdAt', 'desc')),
      );
      const allLogs = logSnap.docs.map((d) => ({ id: d.id, ...d.data() })) as AccessLog[];
      // Hide operator's own testing from every view (recipients, stats, log).
      setLogs(allLogs.filter((l) => !isExcludedEmail(l.email)));
    } catch (err) {
      console.error('Error loading Terra access data:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAll();
  }, []);

  const handleIssue = async (e: FormEvent) => {
    e.preventDefault();
    setFormError('');
    const cleanEmail = email.trim().toLowerCase();
    if (!cleanEmail || !password) {
      setFormError('Email and password are required.');
      return;
    }
    setIssuing(true);
    try {
      const id = await hashCredential(cleanEmail, password);
      await setDoc(doc(db, TERRA_CREDENTIALS, id), {
        email: cleanEmail,
        label: label.trim(),
        password,
        active: true,
        createdAt: serverTimestamp(),
      });
      setIssued({ email: cleanEmail, password });
      setEmail('');
      setLabel('');
      setPassword(genPassword());
      await fetchAll();
    } catch (err) {
      console.error('Error issuing credential:', err);
      setFormError('Could not issue credential. Check console.');
    } finally {
      setIssuing(false);
    }
  };

  const toggleActive = async (cred: Credential) => {
    try {
      await updateDoc(doc(db, TERRA_CREDENTIALS, cred.id), { active: !cred.active });
      setCredentials((cs) =>
        cs.map((c) => (c.id === cred.id ? { ...c, active: !c.active } : c)),
      );
    } catch (err) {
      console.error('Error toggling credential:', err);
    }
  };

  const deleteCredential = async (cred: Credential) => {
    if (!confirm(`Delete the credential for ${cred.email}? They will lose access.`)) return;
    try {
      await deleteDoc(doc(db, TERRA_CREDENTIALS, cred.id));
      setCredentials((cs) => cs.filter((c) => c.id !== cred.id));
    } catch (err) {
      console.error('Error deleting credential:', err);
    }
  };

  const copy = (text: string) => {
    try {
      navigator.clipboard?.writeText(text);
    } catch {
      /* ignore */
    }
  };

  // Set/clear the "email sent" date on a credential (from the inline picker).
  const setSentAt = async (cred: Credential, localValue: string) => {
    const ts = localValue ? Timestamp.fromDate(new Date(localValue)) : null;
    try {
      await updateDoc(doc(db, TERRA_CREDENTIALS, cred.id), { sentAt: ts });
      setCredentials((cs) =>
        cs.map((c) => (c.id === cred.id ? { ...c, sentAt: ts } : c)),
      );
    } catch (err) {
      console.error('Error setting sent date:', err);
    }
  };

  // Last event of a given kind per credential (for the "opened … ago" /
  // "downloaded … ago" columns).
  const lastEventByCredential = (kind: 'unlock' | 'download') =>
    logs.reduce((map, log) => {
      if (!log.success || !log.createdAt) return map;
      const isDownload = log.event === 'download';
      if (kind === 'download' ? !isDownload : isDownload) return map;
      const key = log.credentialId || log.email;
      const cur = map.get(key);
      if (!cur) map.set(key, { last: log.createdAt, count: 1 });
      else {
        cur.count += 1;
        if (log.createdAt.toMillis() > cur.last.toMillis()) cur.last = log.createdAt;
      }
      return map;
    }, new Map<string, { last: Timestamp; count: number }>());

  const opensByCredential = useMemo(() => lastEventByCredential('unlock'), [logs]);
  const downloadsByCredential = useMemo(() => lastEventByCredential('download'), [logs]);

  // Group successful opens by credential to surface shared passwords.
  const recipients = useMemo<RecipientSummary[]>(() => {
    const byCred = new Map<string, RecipientSummary>();
    for (const log of logs) {
      if (!log.success || log.event === 'download') continue;
      const key = log.credentialId || log.email;
      let entry = byCred.get(key);
      if (!entry) {
        entry = {
          credentialId: key,
          email: log.email,
          label: log.label,
          opens: 0,
          ips: [],
          devices: [],
          lastSeen: log.createdAt,
          shared: false,
        };
        byCred.set(key, entry);
      }
      entry.opens += 1;
      const ip = log.ip || 'unknown';
      if (ip !== 'unknown' && !entry.ips.includes(ip)) entry.ips.push(ip);
      const device = [log.browser, log.os, log.deviceType].filter(Boolean).join(' / ');
      if (device && !entry.devices.includes(device)) entry.devices.push(device);
      if (!entry.lastSeen || (log.createdAt && log.createdAt.toMillis() > entry.lastSeen.toMillis())) {
        entry.lastSeen = log.createdAt;
      }
    }
    const list = Array.from(byCred.values());
    for (const r of list) r.shared = r.ips.length > 1 || r.devices.length > 1;
    return list.sort((a, b) => (b.lastSeen?.toMillis() ?? 0) - (a.lastSeen?.toMillis() ?? 0));
  }, [logs]);

  const downloadCount = logs.filter((l) => l.success && l.event === 'download').length;
  const openCount = logs.filter((l) => l.success && l.event !== 'download').length;
  const failCount = logs.filter((l) => !l.success).length;
  const sharedCount = recipients.filter((r) => r.shared).length;

  return (
    <PageWrapper>
      <PageHead title="Terra Access" description="Terra access control." noindex />
      <Container>
        <Header>
          <HeaderContent>
            <HeaderTitle>
              <Icon path={mdiKeyVariant} size={1.2} />
              Terra Access
            </HeaderTitle>
            <HeaderActions>
              <Button variant="ghost" size="sm" onClick={fetchAll}>
                <Icon path={mdiRefresh} size={0.8} />
                Refresh
              </Button>
              <Button as={Link} to="/admin" variant="secondary" size="sm">
                Messages
              </Button>
              <Button variant="ghost" size="sm" onClick={logout}>
                <Icon path={mdiLogout} size={0.8} />
                Logout
              </Button>
            </HeaderActions>
          </HeaderContent>
        </Header>

        <Stats>
          <StatCard>
            <StatValue>{credentials.filter((c) => c.active).length}</StatValue>
            <StatLabel>Active credentials</StatLabel>
          </StatCard>
          <StatCard>
            <StatValue>{openCount}</StatValue>
            <StatLabel>Opens</StatLabel>
          </StatCard>
          <StatCard>
            <StatValue>{downloadCount}</StatValue>
            <StatLabel>Downloads</StatLabel>
          </StatCard>
          <StatCard>
            <StatValue>{failCount}</StatValue>
            <StatLabel>Failed attempts</StatLabel>
          </StatCard>
          <StatCard>
            <StatValue style={sharedCount > 0 ? { color: 'var(--color-error)' } : undefined}>
              {sharedCount}
            </StatValue>
            <StatLabel>Possibly shared</StatLabel>
          </StatCard>
        </Stats>

        <Section>
          <SectionTitle>Issue a credential</SectionTitle>
          <SectionHint>
            Each recipient gets their own email + password. The password is hashed into
            the document id; only this list (admin-only) keeps a readable copy to re-send.
          </SectionHint>
          <GlassCard>
            <IssueForm onSubmit={handleIssue}>
              <Field>
                <Label htmlFor="cred-email">Recipient email</Label>
                <Input
                  id="cred-email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="advisor@firm.com"
                />
              </Field>
              <Field>
                <Label htmlFor="cred-label">Label (optional)</Label>
                <Input
                  id="cred-label"
                  type="text"
                  value={label}
                  onChange={(e) => setLabel(e.target.value)}
                  placeholder="M&A advisor"
                />
              </Field>
              <Field>
                <Label htmlFor="cred-pass">Password</Label>
                <PasswordRow>
                  <Input
                    id="cred-pass"
                    type="text"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                  />
                  <TinyButton type="button" onClick={() => setPassword(genPassword())}>
                    <Icon path={mdiRefresh} size={0.6} />
                  </TinyButton>
                </PasswordRow>
              </Field>
              <Button type="submit" size="sm" disabled={issuing}>
                <Icon path={mdiPlus} size={0.8} />
                {issuing ? 'Issuing…' : 'Issue'}
              </Button>
            </IssueForm>
            {formError && (
              <Banner style={{ background: 'rgba(255,59,48,0.12)', color: 'var(--color-error)' }}>
                {formError}
              </Banner>
            )}
            {issued && (
              <Banner>
                <Icon path={mdiCheckCircle} size={0.8} />
                Issued for {issued.email} — password{' '}
                <Mono>{issued.password}</Mono>
                <TinyButton type="button" onClick={() => copy(issued.password)}>
                  <Icon path={mdiContentCopy} size={0.6} /> copy
                </TinyButton>
              </Banner>
            )}
          </GlassCard>
        </Section>

        <Section>
          <SectionTitle>Credentials</SectionTitle>
          {loading ? (
            <EmptyState>Loading…</EmptyState>
          ) : credentials.length === 0 ? (
            <EmptyState>No credentials issued yet.</EmptyState>
          ) : (
            <GlassCard padding="none">
              <TableWrap>
                <Table>
                  <thead>
                    <tr>
                      <th>Email</th>
                      <th>Label</th>
                      <th>Password</th>
                      <th>Status</th>
                      <th>Sent</th>
                      <th>Opened</th>
                      <th>Downloaded</th>
                      <th>Issued</th>
                      <th></th>
                    </tr>
                  </thead>
                  <tbody>
                    {credentials.map((cred) => (
                      <tr key={cred.id}>
                        <td>{cred.email}</td>
                        <td>{cred.label || '—'}</td>
                        <td>
                          {cred.password ? (
                            <>
                              <Mono>{cred.password}</Mono>{' '}
                              <TinyButton type="button" onClick={() => copy(cred.password!)}>
                                <Icon path={mdiContentCopy} size={0.6} />
                              </TinyButton>
                            </>
                          ) : (
                            '—'
                          )}
                        </td>
                        <td>
                          {cred.active ? (
                            <Pill $tone="ok">active</Pill>
                          ) : (
                            <Pill $tone="muted">revoked</Pill>
                          )}
                        </td>
                        <td>
                          <div style={{ display: 'flex', flexDirection: 'column', gap: '3px' }}>
                            <input
                              type="datetime-local"
                              value={toLocalInput(cred.sentAt)}
                              onChange={(e) => setSentAt(cred, e.target.value)}
                              style={{
                                fontSize: 'var(--text-xs)',
                                padding: '3px 5px',
                                border: '1px solid var(--color-border)',
                                borderRadius: 'var(--radius-sm)',
                                background: 'var(--color-background)',
                                color: 'var(--color-text-primary)',
                                colorScheme: 'dark',
                              }}
                            />
                            <span style={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-tertiary)' }}>
                              {cred.sentAt ? `sent ${relativeTime(cred.sentAt)}` : 'not sent yet'}
                            </span>
                          </div>
                        </td>
                        <td>
                          {(() => {
                            const o = opensByCredential.get(cred.id);
                            return o ? (
                              <span>
                                opened {relativeTime(o.last)}
                                {o.count > 1 ? ` (${o.count}×)` : ''}
                              </span>
                            ) : (
                              <Pill $tone="muted">not opened</Pill>
                            );
                          })()}
                        </td>
                        <td>
                          {(() => {
                            const d = downloadsByCredential.get(cred.id);
                            return d ? (
                              <span>
                                downloaded {relativeTime(d.last)}
                                {d.count > 1 ? ` (${d.count}×)` : ''}
                              </span>
                            ) : (
                              <Pill $tone="muted">no download</Pill>
                            );
                          })()}
                        </td>
                        <td>{formatDate(cred.createdAt)}</td>
                        <td>
                          <TinyButton type="button" onClick={() => toggleActive(cred)}>
                            <Icon path={cred.active ? mdiCancel : mdiCheckCircle} size={0.6} />
                            {cred.active ? 'Revoke' : 'Enable'}
                          </TinyButton>{' '}
                          <TinyButton type="button" onClick={() => deleteCredential(cred)}>
                            <Icon path={mdiDelete} size={0.6} />
                          </TinyButton>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </TableWrap>
            </GlassCard>
          )}
        </Section>

        <Section>
          <SectionTitle>Recipients &amp; sharing</SectionTitle>
          <SectionHint>
            One credential opening from multiple IPs or devices is the signal a password
            was forwarded. Rows flagged in red have more than one distinct IP or device.
          </SectionHint>
          {recipients.length === 0 ? (
            <EmptyState>No opens recorded yet.</EmptyState>
          ) : (
            <GlassCard padding="none">
              <TableWrap>
                <Table>
                  <thead>
                    <tr>
                      <th>Recipient</th>
                      <th>Opens</th>
                      <th>IPs</th>
                      <th>Devices</th>
                      <th>Last seen</th>
                      <th>Flag</th>
                    </tr>
                  </thead>
                  <tbody>
                    {recipients.map((r) => (
                      <tr key={r.credentialId}>
                        <td>
                          {r.email}
                          {r.label ? ` (${r.label})` : ''}
                        </td>
                        <td>{r.opens}</td>
                        <td>
                          <Mono>{r.ips.join(', ') || 'unknown'}</Mono>
                        </td>
                        <td>{r.devices.join(', ') || '—'}</td>
                        <td>{r.lastSeen ? relativeTime(r.lastSeen) : '—'}</td>
                        <td>
                          {r.shared ? (
                            <Pill $tone="warn">
                              <Icon path={mdiAccountAlert} size={0.6} /> shared?
                            </Pill>
                          ) : (
                            <Pill $tone="ok">single</Pill>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </TableWrap>
            </GlassCard>
          )}
        </Section>

        <Section>
          <SectionTitle>Access log</SectionTitle>
          {logs.length === 0 ? (
            <EmptyState>No access attempts recorded yet.</EmptyState>
          ) : (
            <GlassCard padding="none">
              <TableWrap>
                <Table>
                  <thead>
                    <tr>
                      <th>When</th>
                      <th>Email</th>
                      <th>Type</th>
                      <th>Result</th>
                      <th>IP</th>
                      <th>Device</th>
                      <th>Timezone</th>
                    </tr>
                  </thead>
                  <tbody>
                    {logs.map((log) => (
                      <tr key={log.id}>
                        <td>{formatDate(log.createdAt)}</td>
                        <td>{log.email || '—'}</td>
                        <td>{log.event === 'download' ? 'download' : 'unlock'}</td>
                        <td>
                          {log.success ? (
                            <Pill $tone="ok">ok</Pill>
                          ) : (
                            <Pill $tone="warn">fail</Pill>
                          )}
                        </td>
                        <td>
                          <Mono>{log.ip || 'unknown'}</Mono>
                        </td>
                        <td>
                          {[log.browser, log.os, log.deviceType].filter(Boolean).join(' / ') || '—'}
                        </td>
                        <td>{log.timezone || '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </TableWrap>
            </GlassCard>
          )}
        </Section>
      </Container>
    </PageWrapper>
  );
}

export default TerraAccessAdmin;
